import type { ModalPayloadMap, ModalPayload } from '@app/types'
import type {
  AvailableModals,
  InitializedModals,
  VueModalHandlerInterface,
} from '../types'

import BaseModalHandler from './base-modal-handler'
import { mountVueModalInstance } from '../utils'

export default class VueModalHandler
  extends BaseModalHandler
  implements VueModalHandlerInterface
{
  private initializedModals: InitializedModals
  private availableModals: AvailableModals
  private currentMountTarget: HTMLElement | null = null

  constructor(availableModals: AvailableModals) {
    super()
    this.initializedModals = {}
    this.availableModals = availableModals
  }

  open<T extends keyof ModalPayloadMap>(
    modalIdentifier: T,
    params: ModalPayload<T>
  ): void {
    const el = document.getElementById('sf-modal-portal')

    if (el) {
      this.removePreviousMount()

      const { instance, mountTarget } = mountVueModalInstance(
        this.availableModals[modalIdentifier],
        el
      )

      instance.openModal(params)
      this.currentMountTarget = mountTarget
      this._emitModalOpened(modalIdentifier)
    }
  }

  hasVisibleModals(): boolean {
    if (!this.currentMountTarget) return false
    const modal =
      this.currentMountTarget.querySelector<HTMLElement>('.sf-modal')
    return modal !== null && getComputedStyle(modal).display !== 'none'
  }

  close<T extends keyof ModalPayloadMap>(modalIdentifier: T): void {
    const modal = this.initializedModals[modalIdentifier]

    if (modal) {
      delete this.initializedModals[modalIdentifier]
    }
  }

  cleanup(): void {
    this.removePreviousMount()
    Object.keys(this.initializedModals).forEach((modalIdentifier) => {
      this.close(modalIdentifier as keyof ModalPayloadMap)
    })
  }

  // Each open() appends a new child div to #sf-modal-portal.
  // Remove the previous one to avoid accumulating orphaned mount targets.
  private removePreviousMount(): void {
    if (this.currentMountTarget) {
      this.currentMountTarget.remove()
      this.currentMountTarget = null
    }
  }
}
